import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Play,
  Pause,
  Plus as PlusIcon,
  RotateCcw,
  Check,
  Sparkles,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { softBeep, countdownTick, completeChime, primeAudio } from "@/lib/sounds";

function formatBigTime(secs) {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function computeDisplay(task) {
  const stored = task.timer_remaining_seconds ?? (task.duration_minutes || 0) * 60;
  if (!task.timer_running || !task.timer_started_at) return stored;
  const elapsed = Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
  return Math.max(0, stored - elapsed);
}

export default function FocusMode({
  task,
  onClose,
  onUpdate,
  onToggleSubtask,
  onComplete,
  onBreakdown,
}) {
  const [, setTick] = useState(0);
  const lastWarnRef = useRef({ thirty: false, tenSec: -1, done: false });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    primeAudio();
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Force a re-render every second while the timer is running so the big display ticks live.
  useEffect(() => {
    if (!task?.timer_running) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [task?.timer_running]);

  // Reset audio markers whenever timer starts or duration changes
  useEffect(() => {
    lastWarnRef.current = { thirty: false, tenSec: -1, done: false };
  }, [task?.timer_started_at, task?.duration_minutes, task?.id]);

  if (!task) return null;

  const display = computeDisplay(task);
  const isRunning = task.timer_running;
  const hasTimer = !!task.duration_minutes;
  const subtasks = task.subtasks || [];
  const subDone = subtasks.filter((s) => s.completed).length;

  // Audio cues: 30-second warning beep, last-10 countdown ticks, completion chime
  if (hasTimer && isRunning && !lastWarnRef.current.done) {
    if (display === 30 && !lastWarnRef.current.thirty) {
      lastWarnRef.current.thirty = true;
      softBeep();
    }
    if (display > 0 && display <= 10 && lastWarnRef.current.tenSec !== display) {
      lastWarnRef.current.tenSec = display;
      countdownTick();
    }
    if (display === 0) {
      lastWarnRef.current.done = true;
      completeChime();
    }
  }

  const isAlert = hasTimer && display > 0 && display <= 10;

  const handleToggleTimer = () => {
    if (!hasTimer) return;
    if (isRunning) {
      onUpdate({
        timer_running: false,
        timer_remaining_seconds: display,
        timer_started_at: null,
      });
    } else {
      onUpdate({ timer_running: true, timer_started_at: new Date().toISOString() });
    }
  };

  const addSeconds = (secs) => {
    if (!hasTimer) return;
    const stored = task.timer_remaining_seconds ?? task.duration_minutes * 60;
    onUpdate({ timer_remaining_seconds: stored + secs });
  };

  const handleReset = () => {
    if (!hasTimer) return;
    onUpdate({
      timer_running: false,
      timer_started_at: null,
      timer_remaining_seconds: (task.duration_minutes || 0) * 60,
    });
  };

  const isDone = hasTimer && display <= 0;

  return (
    <AnimatePresence>
      <motion.div
        key="focus"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-50 bg-zinc-950 text-white flex flex-col"
        data-testid="focus-mode"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">
              ● Focus mode
            </span>
            <span className="text-xs text-zinc-500 hidden sm:inline">
              Press <kbd className="px-1.5 py-0.5 bg-zinc-800 text-zinc-200 font-mono text-[10px]">ESC</kbd> to exit
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors p-2"
            aria-label="Exit focus mode"
            data-testid="focus-exit-btn"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 overflow-y-auto">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-4">
            {task.category} · {task.priority} priority
          </p>
          <h1
            className="text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-center max-w-3xl mb-12 leading-tight"
            data-testid="focus-title"
          >
            {task.title}
          </h1>

          {/* Timer */}
          {hasTimer ? (
            <div className="flex flex-col items-center gap-6 mb-12">
              <div
                className={`font-mono text-7xl sm:text-8xl md:text-9xl tabular-nums tracking-tight transition-colors ${
                  isAlert || isDone
                    ? "text-red-500 animate-pulse"
                    : isRunning
                    ? "text-white"
                    : "text-zinc-300"
                }`}
                data-testid="focus-timer-display"
              >
                {formatBigTime(display)}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={handleToggleTimer}
                  className="flex items-center gap-2 px-6 py-3 bg-white text-zinc-950 hover:bg-zinc-100 transition-colors font-medium text-sm uppercase tracking-[0.1em]"
                  data-testid="focus-timer-toggle"
                >
                  {isRunning ? (
                    <>
                      <Pause className="w-4 h-4" strokeWidth={2} /> Pause
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" strokeWidth={2} /> Start
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => addSeconds(60)}
                  className="flex items-center gap-1 px-4 py-3 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:border-zinc-500 transition-colors text-sm uppercase tracking-[0.08em]"
                  data-testid="focus-timer-add-min"
                >
                  <PlusIcon className="w-3.5 h-3.5" strokeWidth={2} /> 1 min
                </button>
                <button
                  type="button"
                  onClick={() => addSeconds(30)}
                  className="flex items-center gap-1 px-4 py-3 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:border-zinc-500 transition-colors text-sm uppercase tracking-[0.08em]"
                  data-testid="focus-timer-add-sec"
                >
                  <PlusIcon className="w-3.5 h-3.5" strokeWidth={2} /> 30 sec
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex items-center gap-1 px-4 py-3 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:border-zinc-500 transition-colors text-sm uppercase tracking-[0.08em]"
                  data-testid="focus-timer-reset"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} /> Reset
                </button>
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-1">
                <kbd className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 font-mono">SPACE</kbd> to toggle timer
              </p>
            </div>
          ) : (
            <div className="text-zinc-500 text-sm mb-12">No timer for this task.</div>
          )}

          {/* Subtasks */}
          {subtasks.length > 0 ? (
            <div className="w-full max-w-2xl" data-testid="focus-subtask-list">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-semibold">
                  Subtasks
                </p>
                <span className="text-xs text-zinc-500 font-mono">
                  {subDone}/{subtasks.length}
                </span>
              </div>
              <ul className="border-t border-zinc-800">
                {subtasks.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-3 py-3 border-b border-zinc-800"
                    data-testid={`focus-subtask-${s.id}`}
                  >
                    <Checkbox
                      className="swiss-checkbox border-zinc-600"
                      checked={s.completed}
                      onCheckedChange={(c) => onToggleSubtask(s.id, !!c)}
                      data-testid={`focus-subtask-checkbox-${s.id}`}
                    />
                    <span
                      className={`flex-1 text-base ${
                        s.completed ? "line-through text-zinc-600" : "text-zinc-200"
                      }`}
                    >
                      {s.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="w-full max-w-2xl text-center">
              <button
                type="button"
                onClick={onBreakdown}
                className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:border-blue-500 hover:text-blue-300 transition-colors text-sm uppercase tracking-[0.1em]"
                data-testid="focus-breakdown-btn"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                Break it down with AI
              </button>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-3">
                Or press <kbd className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 font-mono">B</kbd>
              </p>
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className="px-6 md:px-10 py-5 border-t border-zinc-800 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.12em] text-zinc-400 hover:text-white transition-colors"
            data-testid="focus-back-btn"
          >
            ← Back to all tasks
          </button>
          <button
            type="button"
            onClick={() => {
              onComplete();
              onClose();
            }}
            className="flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-500 text-white text-sm uppercase tracking-[0.1em] font-medium transition-colors"
            data-testid="focus-complete-btn"
          >
            <Check className="w-4 h-4" strokeWidth={2} /> Mark done
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
